import React from 'react';
import { Link } from 'react-router-dom';
import {
  Button, Divider, Fade, Grid, Paper, TextField, Typography,
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import accountStyles from '../../pages/account/styles';
import BounceLoader from '../BounceLoader';
import useStyles from './styles';
import analytics from '../../utils/analytics';
import bancameLogo from '../../assets/bancame_logo_dark.svg';
import { postLoginOtpStart, postLoginOtpVerify } from './queries';
import type { LoginOtpErrorState, LoginOtpStep } from './types';

const ERROR_COPY: Record<Exclude<LoginOtpErrorState, null>, string> = {
  captcha: 'Queremos saber si no eres un robot.',
  malformedEmail: 'Revisa el correo: parece que está mal escrito.',
  undeliverableEmail: 'No pudimos enviar el código a ese correo. Revisa que esté bien escrito o prueba con otro.',
  invalidCode: 'El código no es válido o ya venció. Puedes pedir uno nuevo.',
  throttled: 'Espera unos minutos antes de volver a intentar.',
  sendFailed: 'No pudimos enviar el código en este momento. Inténtalo de nuevo en unos minutos.',
  unknown: 'Ocurrió un problema. Inténtalo de nuevo en unos minutos.',
};

function mapStartError(error: unknown): LoginOtpErrorState {
  if (typeof error === 'string') {
    if (error === 'ERR_UNDELIVERABLE_LOGIN_EMAIL') return 'undeliverableEmail';
    return 'malformedEmail';
  }
  if (error === 400) return 'captcha';
  if (error === 429) return 'throttled';
  if (error === 503) return 'sendFailed';
  return 'unknown';
}

function mapVerifyError(error: unknown): LoginOtpErrorState {
  if (typeof error === 'string') return 'malformedEmail';
  if (error === 400) return 'invalidCode';
  if (error === 429) return 'throttled';
  return 'unknown';
}

type LoginOtpProps = {
  onSuccess: (session: Awaited<ReturnType<typeof postLoginOtpVerify>>) => void;
};

export default function LoginOtp({ onSuccess }: LoginOtpProps) {
  const accountClasses = accountStyles();
  const classes = useStyles();
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);
  const [step, setStep] = React.useState<LoginOtpStep>('email');
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [notRegistered, setNotRegistered] = React.useState(false);
  const [error, setError] = React.useState<LoginOtpErrorState>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    analytics.page('PU - LOGIN', 'INGRESO CON CODIGO');
  }, []);

  React.useEffect(() => {
    if (notRegistered) analytics.page('PU - LOGIN', 'CORREO NO REGISTRADO');
  }, [notRegistered]);

  React.useEffect(() => {
    if (step === 'code') analytics.page('PU - LOGIN', 'CODIGO ENVIADO');
  }, [step]);

  const handleStart = async () => {
    setLoading(true);
    setError(null);
    setNotRegistered(false);
    const captchaToken = recaptchaRef.current?.getValue();
    if (!captchaToken) {
      setError('captcha');
      setLoading(false);
      return;
    }
    try {
      const { registered } = await postLoginOtpStart(email, captchaToken);
      if (registered) {
        setStep('code');
      } else {
        setNotRegistered(true);
      }
    } catch (err) {
      setError(mapStartError(err));
    } finally {
      recaptchaRef.current?.reset();
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    setError(null);
    try {
      const session = await postLoginOtpVerify(email, code);
      analytics.identify(session.identificationId);
      analytics.page('PU - LOGIN', 'INGRESO CON CODIGO EXITOSO');
      onSuccess(session);
    } catch (err) {
      setError(mapVerifyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in {...({ timeout: 1000 })}>
      <Grid
        item
        xs={12}
        lg={5}
        md={7}
        sm={10}
        className={accountClasses.paper}
        component={Paper}
        elevation={4}
        borderRadius={5}
      >
        <div className={classes.content}>
          <div style={{ marginBottom: 10, textAlign: 'center' }}>
            <object data={bancameLogo} width="140px" aria-labelledby="banca.me logo" />
          </div>
          <Divider />
          <br />
          <Typography variant="h4" style={{ marginBottom: 12 }}>
            {step === 'email' ? 'Entra con un código' : 'Revisa tu correo'}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 28 }}>
            {step === 'email'
              ? 'Te enviamos un código al correo que ya tienes registrado con nosotros.'
              : 'Te enviamos un código de 6 dígitos. Puede tardar un momento en llegar.'}
          </Typography>

          {step === 'email' ? (
            <>
              <Typography gutterBottom variant="body1" textAlign="left">Correo</Typography>
              <TextField
                fullWidth
                variant="outlined"
                type="email"
                placeholder="Ingresa tu correo"
                value={email}
                disabled={loading}
                onChange={(e) => setEmail(e.target.value)}
              />
              <ReCAPTCHA
                className={classes.recaptchaContainer}
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_CAPTCHA_PLRV2 as string}
              />
            </>
          ) : (
            <>
              <Typography gutterBottom variant="body1" textAlign="left">Código</Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="6 dígitos"
                value={code}
                disabled={loading}
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                onChange={(e) => setCode(e.target.value)}
              />
            </>
          )}

          <div className={classes.errorRegion} aria-live="polite">
            {error && (
              <Typography color="error" variant="body2">{ERROR_COPY[error]}</Typography>
            )}
            {notRegistered && (
              <>
                <Typography color="error" variant="body2" gutterBottom>
                  Ese correo no está registrado con nosotros. Revisa que esté bien escrito.
                </Typography>
                <Typography variant="body2">
                  ¿Tienes cuenta pero nunca creaste contraseña?
                  {' '}
                  <Link to="/account/recover-password">Créala acá</Link>
                </Typography>
                <Typography variant="body2">
                  ¿Todavía no tienes cuenta?
                  {' '}
                  <Link to="/account/register">Regístrate</Link>
                </Typography>
              </>
            )}
          </div>

          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            className={loading ? classes.loadingButton : undefined}
            onClick={step === 'email' ? handleStart : handleVerify}
          >
            {loading
              ? <BounceLoader size={16} color="#FFFFFF" />
              : <span>{step === 'email' ? 'Enviarme el código' : 'Entrar'}</span>}
          </Button>

          {step === 'code' && (
            <div className={classes.secondaryActions}>
              <Button variant="text" disabled={loading} onClick={() => setStep('email')}>
                Reenviar código
              </Button>
              <Typography variant="body2">
                ¿No te llegó el código?
                {' '}
                <Link to="/account/login">Entra con tu contraseña</Link>
              </Typography>
            </div>
          )}
        </div>
      </Grid>
    </Fade>
  );
}
